import { Divider } from '@mantine/core'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Banner } from 'src/components/Banner/Banner'
import { DonationRibbon } from 'src/components/DonationRibbon/DonationRibbon'
import { ProductBreadCrumbs } from 'src/components/ProductDetail/ProductBreadCrumbs'
import { ProductInfo } from 'src/components/ProductDetail/ProductInfo'
import { ProductPhotos } from 'src/components/ProductDetail/ProductPhotos'
import { ProductDetailSchema } from 'src/components/ProductSchema/ProductDetailSchema'
import { SimilarProducts } from 'src/components/SimilarProducts/SimilarProducts'
import { useLocale } from 'src/contexts/LocaleContext'
import { Language } from 'src/enum/Language'
import { getProductDetail as getProductDetailDataLayer } from 'src/functions/util/dataLayer'
import { useCartQuery } from 'src/hooks/useCartQuery'

import styles from './ProductDetail.module.scss'

import type { Collection, Product } from 'src/data/graphql-generated'
import type { ProductReview, ProductRating } from 'src/types/Product'

interface ProductDetailProps {
  product: Product
  rating: NonNullable<ProductRating>
  reviews?: ProductReview[]
  recommendedProducts: Product[]
  breadcrumbs: (Collection | undefined)[]
}

export const ProductDetail = ({
  product,
  rating,
  reviews = [],
  recommendedProducts = [],
  breadcrumbs,
}: ProductDetailProps) => {
  const { language } = useLocale()
  const { query } = useRouter()
  const {
    isFetched: cartIsFetched,
    computed: { isLoggedIn },
  } = useCartQuery()
  const { t } = useTranslation()

  const {
    productType,
    title,
    variants: { nodes: productVariants },
  } = product

  const [mainVariant] = productVariants
  const [variantSku, setVariantSku] = useState(mainVariant.sku)

  const variant = productVariants.find(({ sku }) => sku === variantSku) || mainVariant

  useEffect(() => {
    if (query.number) {
      setVariantSku(query.number as string)
    }
  }, [query.number])

  useEffect(() => {
    if (cartIsFetched) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(getProductDetailDataLayer(product, variant, isLoggedIn))
    }
  }, [product, variant, cartIsFetched])

  return (
    <div>
      {query.referrer === 'coffee-match' && (
        <Banner
          link={{
            url: `/${language}/e/${language === Language.de ? 'lieblingskaffee' : 'coffee-match'}`,
            text: t('banner.coffeeMatch.linkText'),
          }}
          headline={t('banner.coffeeMatch.headline')}
          subText={t('banner.coffeeMatch.subText')}
        />
      )}

      <div className={styles.container}>
        <ProductBreadCrumbs breadcrumbs={breadcrumbs} hidden="desktop" />

        <div className={styles.wrapper}>
          <div className={styles.productColumn}>
            <ProductPhotos productName={title} variant={variant} />
          </div>

          <div className={styles.productColumn}>
            <ProductInfo
              product={product}
              variant={variant}
              setVariantSku={setVariantSku}
              rating={rating}
              breadcrumbs={breadcrumbs}
            />
          </div>
        </div>
      </div>

      <Divider visibleFrom="lg" />

      <SimilarProducts products={recommendedProducts} productType={productType} />

      <DonationRibbon />

      <ProductDetailSchema product={product} rating={rating} reviews={reviews} />
    </div>
  )
}
